import { useState, useCallback } from 'react';
import type { ContentData } from '../lib/types';
import { fetchContent, publishContent } from '../lib/api';

interface UseContentReturn {
  content: ContentData | null;
  loading: boolean;
  error: string | null;
  load: (path: string) => Promise<void>;
  publish: (path: string, markdown: string, message?: string) => Promise<{ commitSha: string }>;
  publishing: boolean;
}

export function useContent(): UseContentReturn {
  const [content, setContent] = useState<ContentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchContent(path);
      setContent(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  }, []);

  const publish = useCallback(
    async (path: string, markdown: string, message?: string) => {
      setPublishing(true);
      try {
        const result = await publishContent(path, markdown, message);
        return { commitSha: result.commitSha };
      } finally {
        setPublishing(false);
      }
    },
    []
  );

  return { content, loading, error, load, publish, publishing };
}
